import { c as _c } from "react/compiler-runtime";
import { useFormikContext } from 'formik';
import { CLI_TOKEN_EXPIRY_DAYS } from '../../../../../../domain/cli-token-access';
import { CliKeyAccessInput } from '../../../../../../typings/CliClient.interface';
import Text from '../base/Text';
import RadioCardsInput from '../Form/Fields/RadioCardsInput';
import SelectInput from '../Form/Fields/SelectInput';
import { Div } from '../helpers/StyledUtils';
import { ProjectAccessFields, ProjectAccessFormValues } from './ProjectAccessFields';

// What a CLI key can reach and for how long. Picked when a key is made on the Keys & Sessions page
// and when a `hookdeck login` is approved.

const NEVER_EXPIRES = 'never';
const EXPIRY_LABELS: Record<(typeof CLI_TOKEN_EXPIRY_DAYS)[number], string> = {
  7: '7 days',
  30: '30 days',
  90: '90 days',
  365: '1 year'
};
const EXPIRY_OPTIONS = [{
  value: NEVER_EXPIRES,
  label: 'Never'
}, ...CLI_TOKEN_EXPIRY_DAYS.map(days => ({
  value: String(days),
  label: EXPIRY_LABELS[days]
}))];
const ACCESS_OPTIONS = [{
  value: 'full',
  label: 'Full access',
  description: 'Everything you can do, in all your organizations.'
}, {
  value: 'organization',
  label: 'One organization',
  description: 'Only one organization, or some of its projects.'
}];
export interface CliKeyAccessFormValues extends ProjectAccessFormValues {
  expires_in: string;
  access: string;
  organization_id: string;
}
interface AccessOrganization {
  id: string;
  name: string;
}
interface AccessProject {
  id: string;
  name: string;
  organization_id: string;
}

/** Full access, never expires. The organization is the one picked if the access is limited. */
export const cliKeyAccessInitialValues = (organization_id?: string): CliKeyAccessFormValues => ({
  expires_in: NEVER_EXPIRES,
  access: 'full',
  organization_id: organization_id ?? '',
  project_access: 'all',
  project_ids: []
});
const projectsOf = (projects: AccessProject[], organization_id: string) => projects.filter(project => project.organization_id === organization_id);

// Only the selected organization's projects count, in case some were picked in another one.
const selectedProjectIds = (values: CliKeyAccessFormValues, projects: AccessProject[]) => {
  const organization_project_ids = projectsOf(projects, values.organization_id).map(({
    id
  }) => id);
  return values.project_ids.filter(id => organization_project_ids.includes(id));
};
export const validateCliKeyAccess = (values: CliKeyAccessFormValues, projects: AccessProject[]): Partial<Record<keyof CliKeyAccessFormValues, string>> => {
  const errors: Partial<Record<keyof CliKeyAccessFormValues, string>> = {};
  if (values.access === 'organization') {
    if (!values.organization_id) {
      errors.organization_id = 'Required';
    } else if (values.project_access === 'selected' && selectedProjectIds(values, projects).length === 0) {
      errors.project_ids = 'Select at least one project';
    }
  }
  return errors;
};

/** The picked access, as the API takes it. */
export const toCliKeyAccessInput = (values: CliKeyAccessFormValues, projects: AccessProject[]): Required<CliKeyAccessInput> => {
  const is_limited = values.access === 'organization';
  return {
    expires_in_days: values.expires_in === NEVER_EXPIRES ? null : Number(values.expires_in) as (typeof CLI_TOKEN_EXPIRY_DAYS)[number],
    organization_id: is_limited ? values.organization_id : null,
    team_ids: is_limited && values.project_access === 'selected' ? selectedProjectIds(values, projects) : null
  };
};
export interface CliKeyAccessLabel {
  label: string;
  // The full access, shown on hover when the key is limited.
  details?: string;
}

/** What a key reaches: "Full access", its organization, or its organization and projects. */
export const getCliKeyAccess = (cli_key: Pick<CliKeyAccessInput, 'organization_id' | 'team_ids'>, organization_names: Map<string, string>, project_names: Map<string, string>): CliKeyAccessLabel => {
  if (!cli_key.organization_id) {
    return {
      label: 'Full access'
    };
  }
  const organization_name = organization_names.get(cli_key.organization_id) ?? 'One organization';
  if (!cli_key.team_ids) {
    return {
      label: organization_name,
      details: `All projects in ${organization_name}`
    };
  }
  const names = cli_key.team_ids.map(team_id => project_names.get(team_id) ?? team_id);
  const project_label = cli_key.team_ids.length === 1 ? names[0] : `${cli_key.team_ids.length} projects`;
  return {
    label: `${organization_name} · ${project_label}`,
    details: `${organization_name}: ${names.join(', ')}`
  };
};

/** How long the key lasts, as `expires_in` in a Formik form. */
export const CliKeyExpiryField: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SelectInput name="expires_in" label="Expiration" options={EXPIRY_OPTIONS} block={true} m={{
      b: 4
    }} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
interface CliKeyAccessFieldsProps {
  organizations: AccessOrganization[];
  projects: AccessProject[];
  current_project_id?: string;
}

/** Access, then expiration, inside a Formik form whose values include CliKeyAccessFormValues. */
export const CliKeyAccessFields: React.FC<CliKeyAccessFieldsProps> = t0 => {
  const $ = _c(12);
  const {
    organizations,
    projects,
    current_project_id
  } = t0;
  const {
    values
  } = useFormikContext();
  let t1;
  if ($[0] !== organizations) {
    t1 = organizations.map(_temp);
    $[0] = organizations;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const organization_options = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Div flex={{
      direction: "column",
      gap: 0
    }} m={{
      b: 4
    }}><Text semi={true} size="xs" muted={true} as="label">Access</Text><RadioCardsInput name="access" options={ACCESS_OPTIONS} /></Div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== current_project_id || $[4] !== organization_options || $[5] !== projects || $[6] !== values.access || $[7] !== values.organization_id) {
    t3 = values.access === "organization" && <><SelectInput name="organization_id" label="Organization" options={organization_options} block={true} m={{
        b: 4
      }} /><Div m={{
        b: 4
      }}><ProjectAccessFields projects={projectsOf(projects, values.organization_id)} current_project_id={current_project_id} /></Div></>;
    $[3] = current_project_id;
    $[4] = organization_options;
    $[5] = projects;
    $[6] = values.access;
    $[7] = values.organization_id;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CliKeyExpiryField />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3) {
    t5 = <>{t2}{t3}{t4}</>;
    $[10] = t3;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
function _temp(organization) {
  return {
    value: organization.id,
    label: organization.name
  };
}